/**
 * Attention Queue Hook - Aggregates actionable items for the Smart Queue Dashboard
 *
 * Combines data from multiple sources:
 * - Upcoming earnings (7 days)
 * - Thesis allocation drift (warning/alert status)
 * - Material negative news
 * - Recently triggered alerts
 */
import { useMemo } from 'react'
import { useEarningsCalendar } from './useEarnings'
import { useThesisAllocations } from './useThesis'
import { useMaterialNews } from './useNews'
import { useRecentAlerts } from './useAlerts'
import type { EarningsCalendarEntry } from '../types/earnings'
import type { ThesisAllocation, DriftStatus } from '../types/thesis'
import type { NewsArticleListItem } from '../types/news'
import type { RecentAlert } from '../types/alerts'

// Attention item types for the queue
export type AttentionCategory = 'earnings' | 'drift' | 'news' | 'alert'

export interface AttentionItem {
  id: string
  category: AttentionCategory
  priority: 'urgent' | 'high' | 'normal' | 'low'
  title: string
  subtitle?: string
  symbol?: string
  actionUrl?: string
  timestamp?: string
  // Original data for rendering details
  data: EarningsCalendarEntry | ThesisAllocation | NewsArticleListItem | RecentAlert
}

// Priority weights for sorting
const PRIORITY_WEIGHTS: Record<AttentionItem['priority'], number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
}

// Convert drift status to priority
function driftToPriority(status: DriftStatus): AttentionItem['priority'] {
  switch (status) {
    case 'alert':
      return 'high'
    case 'warning':
      return 'normal'
    default:
      return 'low'
  }
}

// Convert days until earnings to priority
function earningsToPriority(daysUntil: number): AttentionItem['priority'] {
  if (daysUntil <= 1) return 'urgent'
  if (daysUntil <= 3) return 'high'
  if (daysUntil <= 7) return 'normal'
  return 'low'
}

// Convert alert priority string to typed priority
function alertToPriority(priority?: string): AttentionItem['priority'] {
  if (priority === 'urgent') return 'urgent'
  if (priority === 'high') return 'high'
  if (priority === 'normal') return 'normal'
  return 'low'
}

export interface UseAttentionQueueOptions {
  /** Max earnings days ahead to show (default: 7) */
  earningsDays?: number
  /** Max news days to look back (default: 3) */
  newsDays?: number
  /** Max alerts days to look back (default: 7) */
  alertsDays?: number
  /** Max total items to return (default: 20) */
  limit?: number
}

export interface AttentionQueueResult {
  items: AttentionItem[]
  isLoading: boolean
  error: Error | null
  counts: {
    earnings: number
    drift: number
    news: number
    alerts: number
    total: number
  }
}

export function useAttentionQueue(options: UseAttentionQueueOptions = {}): AttentionQueueResult {
  const {
    earningsDays = 7,
    newsDays = 3,
    alertsDays = 7,
    limit = 20,
  } = options

  // Fetch all data sources
  const earnings = useEarningsCalendar(earningsDays)
  const allocations = useThesisAllocations()
  const news = useMaterialNews({ days: newsDays })
  const alerts = useRecentAlerts({ days: alertsDays, limit: 50 })

  // Aggregate into attention items
  const result = useMemo<AttentionQueueResult>(() => {
    const items: AttentionItem[] = []

    // Process earnings (filter to 7 days)
    if (earnings.data?.entries) {
      for (const entry of earnings.data.entries) {
        if (entry.days_until <= earningsDays) {
          items.push({
            id: `earnings-${entry.symbol}-${entry.report_date}`,
            category: 'earnings',
            priority: earningsToPriority(entry.days_until),
            title: `${entry.symbol} earnings`,
            subtitle: entry.days_until === 0
              ? 'Today'
              : entry.days_until === 1
                ? 'Tomorrow'
                : `In ${entry.days_until} days`,
            symbol: entry.symbol,
            actionUrl: `/research/${encodeURIComponent(entry.symbol)}/earnings`,
            timestamp: entry.report_date,
            data: entry,
          })
        }
      }
    }

    // Process thesis drift (only warning/alert status)
    if (allocations.data?.allocations) {
      for (const alloc of allocations.data.allocations) {
        if (alloc.drift_status !== 'ok' && alloc.target_allocation_pct !== null) {
          const overUnder = alloc.drift_pct > 0 ? 'over' : 'under'
          items.push({
            id: `drift-${alloc.thesis_id}`,
            category: 'drift',
            priority: driftToPriority(alloc.drift_status),
            title: `${alloc.thesis_name} drift`,
            subtitle: `${Math.abs(alloc.drift_pct).toFixed(1)}% ${overUnder} target`,
            actionUrl: '/strategy/allocations',
            data: alloc,
          })
        }
      }
    }

    // Process material negative news
    if (news.data?.articles) {
      for (const article of news.data.articles) {
        if (article.sentiment === 'negative' && article.is_material) {
          items.push({
            id: `news-${article.id}`,
            category: 'news',
            priority: 'normal', // Material news is always at least normal priority
            title: article.headline,
            subtitle: article.symbol ? `${article.symbol} - ${article.source}` : article.source,
            symbol: article.symbol || undefined,
            actionUrl: article.symbol ? `/research/${encodeURIComponent(article.symbol)}/news` : undefined,
            timestamp: article.published_at,
            data: article,
          })
        }
      }
    }

    // Process recent alerts
    if (alerts.data?.alerts) {
      for (const alert of alerts.data.alerts) {
        items.push({
          id: `alert-${alert.id}`,
          category: 'alert',
          priority: alertToPriority(alert.priority),
          title: alert.rule_name || alert.alert_type,
          subtitle: alert.thesis_name
            ? `${alert.symbol} - ${alert.thesis_name}`
            : alert.symbol,
          symbol: alert.symbol,
          actionUrl: `/research/${encodeURIComponent(alert.symbol)}`,
          timestamp: alert.triggered_at,
          data: alert,
        })
      }
    }

    // Sort by priority, then by timestamp (most recent first)
    items.sort((a, b) => {
      const priorityDiff = PRIORITY_WEIGHTS[a.priority] - PRIORITY_WEIGHTS[b.priority]
      if (priorityDiff !== 0) return priorityDiff

      // If same priority, sort by timestamp (most recent first)
      if (a.timestamp && b.timestamp) {
        return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      }
      return 0
    })

    // Calculate counts before limiting
    const counts = {
      earnings: items.filter((i) => i.category === 'earnings').length,
      drift: items.filter((i) => i.category === 'drift').length,
      news: items.filter((i) => i.category === 'news').length,
      alerts: items.filter((i) => i.category === 'alert').length,
      total: items.length,
    }

    return {
      items: items.slice(0, limit),
      isLoading: earnings.isLoading || allocations.isLoading || news.isLoading || alerts.isLoading,
      error: earnings.error || allocations.error || news.error || alerts.error,
      counts,
    }
  }, [
    earnings.data,
    earnings.isLoading,
    earnings.error,
    allocations.data,
    allocations.isLoading,
    allocations.error,
    news.data,
    news.isLoading,
    news.error,
    alerts.data,
    alerts.isLoading,
    alerts.error,
    earningsDays,
    limit,
  ])

  return result
}
